import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import MainLayout from '../components/layout/MainLayout';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_URL = process.env.REACT_APP_API_URL;

function getToken() {
  return localStorage.getItem('fhc_token') || '';
}

function formatTaille(bytes) {
  if (bytes < 1024) return bytes + ' o';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
  return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
}

function formatDate(d) {
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const MIME_ICONS = {
  'application/pdf': '📄',
  'image/jpeg': '🖼️',
  'image/png': '🖼️',
  'application/msword': '📝',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '📝',
  'application/vnd.ms-excel': '📊',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '📊',
};

const s = {
  root: { display: 'flex', flexDirection: 'column', gap: 0, height: '100%' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px 14px', borderBottom: '1px solid rgba(255,255,255,.06)' },
  title: { fontSize: 18, fontWeight: 700, color: '#fff' },
  sub: { fontSize: 11, color: '#667', marginTop: 2 },
  body: { display: 'flex', flex: 1, overflow: 'hidden' },
  sidebar: { width: 240, borderRight: '1px solid rgba(255,255,255,.06)', padding: '16px 0', overflowY: 'auto', flexShrink: 0 },
  sideSection: { padding: '0 12px', marginBottom: 8 },
  sideLabel: { fontSize: 9, color: '#556', textTransform: 'uppercase', letterSpacing: '.8px', padding: '4px 8px', marginBottom: 4 },
  sideItem: (active) => ({
    padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: active ? 600 : 400,
    color: active ? '#C9A84C' : '#aaa', background: active ? 'rgba(201,168,76,.08)' : 'transparent',
    borderLeft: active ? '2px solid #C9A84C' : '2px solid transparent', marginBottom: 2,
    transition: 'all .15s',
  }),
  deptItem: (active) => ({
    padding: '6px 12px 6px 24px', borderRadius: 8, cursor: 'pointer', fontSize: 11,
    color: active ? '#64b5f6' : '#778', background: active ? 'rgba(100,181,246,.08)' : 'transparent',
    borderLeft: active ? '2px solid #64b5f6' : '2px solid transparent', marginBottom: 1,
  }),
  main: { flex: 1, padding: '20px 24px', overflowY: 'auto' },
  toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  btnGold: { background: 'linear-gradient(135deg,#C9A84C,#a07830)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnOutline: { background: 'transparent', color: '#aaa', border: '1px solid rgba(255,255,255,.12)', borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: 'pointer' },
  btnDanger: { background: 'rgba(239,83,80,.1)', color: '#ef8080', border: '1px solid rgba(239,83,80,.2)', borderRadius: 6, padding: '5px 10px', fontSize: 11, cursor: 'pointer' },
  btnBlue: { background: 'rgba(100,181,246,.1)', color: '#64b5f6', border: '1px solid rgba(100,181,246,.2)', borderRadius: 6, padding: '5px 10px', fontSize: 11, cursor: 'pointer' },
  card: { background: '#162535', border: '1px solid rgba(255,255,255,.06)', borderRadius: 10, padding: '14px 16px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 14 },
  docIcon: { fontSize: 28, flexShrink: 0 },
  docInfo: { flex: 1, minWidth: 0 },
  docName: { fontSize: 13, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  docMeta: { fontSize: 10, color: '#667', marginTop: 3 },
  docActions: { display: 'flex', gap: 6, flexShrink: 0 },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#445', fontSize: 13 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#0d1825', border: '1px solid rgba(255,255,255,.1)', borderRadius: 14, padding: 28, width: 480, maxWidth: '95vw' },
  modalTitle: { fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 20 },
  field: { marginBottom: 14 },
  label: { fontSize: 10, color: '#667', textTransform: 'uppercase', letterSpacing: '.7px', display: 'block', marginBottom: 5 },
  input: { width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  select: { width: '100%', background: '#0d1b2a', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  err: { background: 'rgba(239,83,80,.1)', border: '1px solid rgba(239,83,80,.3)', borderRadius: 8, padding: '8px 12px', color: '#ef8080', fontSize: 12, marginBottom: 12 },
  previewOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.88)', zIndex: 1100, display: 'flex', flexDirection: 'column' },
  previewHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,.08)', flexShrink: 0 },
  previewBody: { flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  btnGreen: { background: 'rgba(102,187,106,.1)', color: '#66bb6a', border: '1px solid rgba(102,187,106,.2)', borderRadius: 6, padding: '5px 10px', fontSize: 11, cursor: 'pointer' },
};

export default function DocumentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filiales, setFiliales] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedFiliale, setSelectedFiliale] = useState(null);
  const [selectedDept, setSelectedDept] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ nom: '', description: '', filiale_id: '', departement_id: '' });
  const [fichiers, setFichiers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef();
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [accesDoc, setAccesDoc] = useState(null); // document dont on gère les accès
  const [accesListe, setAccesListe] = useState([]);
  const [accesUsers, setAccesUsers] = useState([]);
  const [accesSearch, setAccesSearch] = useState('');
  const [accesLoading, setAccesLoading] = useState(false);

  const isDG = ['dg', 'admin'].includes(user?.role);
  const peutGerer = ['dg', 'admin', 'chef_departement', 'chef'].includes(user?.role);
  const peutVoir = true; // tous les utilisateurs connectés peuvent visualiser

  useEffect(() => {
    // Toujours charger filiales et départements pour la sidebar
    api.filiales.getAll().then(r => setFiliales(r.filiales || r.data || r || [])).catch(() => {});
    api.departements.getAll().then(r => setDepartements(r.departements || r.data || r || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (isDG) {
      if (selectedFiliale) params.append('filiale_id', selectedFiliale);
      if (selectedDept) params.append('departement_id', selectedDept);
      if (!selectedFiliale) { setDocuments([]); setLoading(false); return; }
    }
    fetch(`${API_URL}/documents?${params}`, { headers: { Authorization: 'Bearer ' + getToken() } })
      .then(r => r.json())
      .then(r => setDocuments(r.documents || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isDG, selectedFiliale, selectedDept]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line no-unused-vars
  const deptsDuFiliale = departements.filter(d => String(d.filiale_id) === String(selectedFiliale));

  const openModal = () => {
    setForm({ nom: '', description: '', filiale_id: selectedFiliale || '', departement_id: selectedDept || '' });
    setFichiers([]);
    setErr('');
    setShowModal(true);
  };

  const handleUpload = async () => {
    if (!form.filiale_id || fichiers.length === 0) {
      setErr('Filiale et au moins un fichier sont obligatoires.');
      return;
    }
    if (fichiers.length === 1 && !form.nom.trim()) {
      setErr('Le nom du document est obligatoire.');
      return;
    }
    setUploading(true);
    setErr('');
    try {
      if (fichiers.length === 1) {
        // Route simple existante
        const fd = new FormData();
        fd.append('nom', form.nom);
        fd.append('description', form.description);
        fd.append('filiale_id', form.filiale_id);
        if (form.departement_id) fd.append('departement_id', form.departement_id);
        fd.append('fichier', fichiers[0]);
        const res = await fetch(`${API_URL}/documents`, {
          method: 'POST', headers: { Authorization: 'Bearer ' + getToken() }, body: fd,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Erreur');
        setDocuments(prev => [data.document, ...prev]);
      } else {
        // Route multiple
        const fd = new FormData();
        fichiers.forEach((f, i) => {
          fd.append('fichiers[]', f);
          const nom = form.nom ? `${form.nom} (${i + 1})` : f.name.replace(/\.[^.]+$/, '');
          fd.append('noms[]', nom);
        });
        fd.append('description', form.description);
        fd.append('filiale_id', form.filiale_id);
        if (form.departement_id) fd.append('departement_id', form.departement_id);
        const res = await fetch(`${API_URL}/documents/multiple`, {
          method: 'POST', headers: { Authorization: 'Bearer ' + getToken() }, body: fd,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Erreur');
        setDocuments(prev => [...(data.documents || []), ...prev]);
      }
      setShowModal(false);
    } catch (e) {
      setErr(e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await fetch(`${API_URL}/documents/${id}`, {
        method: 'DELETE',
        headers: { Authorization: 'Bearer ' + getToken() },
      });
      setDocuments(prev => prev.filter(d => d.id !== id));
    } catch {}
  };

  const handleDownload = (doc) => {
    const a = document.createElement('a');
    fetch(`${API_URL}/documents/${doc.id}/download`, {
      headers: { Authorization: 'Bearer ' + getToken() },
    })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        a.href = url;
        a.setAttribute('download', doc.nom_fichier);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
  };

  const PREVIEW_TYPES = ['image/', 'application/pdf'];
  const OFFICE_TYPES = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ];

  const isPreviewable = (mime) => mime?.startsWith('image/') || mime === 'application/pdf';
  const isOffice = (mime) => OFFICE_TYPES.includes(mime);

  const getFilePublicUrl = (doc) => {
    const base = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
    return `${base}/uploads/${doc.chemin_fichier}`;
  };

  const handlePreview = (doc) => {
    setPreviewDoc(doc);
    setPreviewUrl(null);
    if (isOffice(doc.type_mime)) { return; }
    if (!isPreviewable(doc.type_mime)) { return; }
    setPreviewLoading(true);
    fetch(`${API_URL}/documents/${doc.id}/preview`, {
      headers: { Authorization: 'Bearer ' + getToken() },
    })
      .then(r => r.blob())
      .then(blob => {
        const typed = new Blob([blob], { type: doc.type_mime });
        setPreviewUrl(URL.createObjectURL(typed));
      })
      .finally(() => setPreviewLoading(false));
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null);
    setPreviewUrl(null);
    setNumPages(null);
    setPageNumber(1);
  };

  const openAcces = async (doc) => {
    setAccesDoc(doc);
    setAccesSearch('');
    setAccesLoading(true);
    try {
      const [resAcces, resUsers] = await Promise.all([
        fetch(`${API_URL}/documents/${doc.id}/acces`, { headers: { Authorization: 'Bearer ' + getToken() } }).then(r => r.json()),
        fetch(`${API_URL}/users`, { headers: { Authorization: 'Bearer ' + getToken() } }).then(r => r.json()),
      ]);
      setAccesListe(resAcces.acces || []);
      setAccesUsers(resUsers.users || resUsers.data || resUsers || []);
    } catch {}
    setAccesLoading(false);
  };

  const toggleAcces = async (userId, hasAcces) => {
    await fetch(`${API_URL}/documents/${accesDoc.id}/acces`, {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + getToken(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, action: hasAcces ? 'revoquer' : 'accorder' }),
    });
    setAccesListe(prev =>
      hasAcces ? prev.filter(u => u.id !== userId)
               : [...prev, accesUsers.find(u => u.id === userId)]
    );
  };

  const filialeCourante = filiales.find(f => String(f.id) === String(selectedFiliale));
  const deptCourant = departements.find(d => String(d.id) === String(selectedDept));

  return (
    <MainLayout>
      <div style={s.root}>
        {/* HEADER */}
        <div style={s.header}>
          <div>
            <div style={s.title}>📁 Gestion des Documents</div>
            <div style={s.sub}>
              {isDG
                ? (filialeCourante ? filialeCourante.nom : 'Toutes les filiales') + (deptCourant ? ' › ' + deptCourant.nom : '')
                : (() => {
                    const myDept = departements.find(d =>
                      String(d.id) === String(user?.departement_id) ||
                      String(d.id) === String(user?.employee?.departement_id)
                    );
                    return myDept?.nom || 'Mon département';
                  })()
              }
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={s.btnOutline} onClick={() => navigate(-1)}>← Retour</button>
            <button style={s.btnGold} onClick={openModal}>+ Ajouter un document</button>
          </div>
        </div>

        <div style={s.body}>
          {/* SIDEBAR */}
          <div style={s.sidebar}>
            <div style={s.sideSection}>
              {isDG ? (
                <>
                  <div style={s.sideLabel}>Filiales</div>
                  <div
                    style={s.sideItem(!selectedFiliale)}
                    onClick={() => { setSelectedFiliale(null); setSelectedDept(null); }}
                  >
                    🏢 Toutes les filiales
                  </div>
                  {filiales.map(f => (
                    <div key={f.id}>
                      <div
                        style={s.sideItem(String(selectedFiliale) === String(f.id))}
                        onClick={() => { setSelectedFiliale(f.id); setSelectedDept(null); }}
                      >
                        🏬 {f.nom}
                      </div>
                      {String(selectedFiliale) === String(f.id) && departements
                        .filter(d => String(d.filiale_id) === String(f.id))
                        .map(d => (
                          <div
                            key={d.id}
                            style={s.deptItem(String(selectedDept) === String(d.id))}
                            onClick={() => setSelectedDept(String(selectedDept) === String(d.id) ? null : d.id)}
                          >
                            📂 {d.nom}
                          </div>
                        ))}
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div style={s.sideLabel}>Mon Département</div>
                  {(() => {
                    const myDept = departements.find(d =>
                      String(d.id) === String(user?.departement_id) ||
                      String(d.id) === String(user?.employee?.departement_id)
                    );
                    const myFiliale = myDept ? filiales.find(f => String(f.id) === String(myDept.filiale_id)) : null;
                    return (
                      <>
                        {myFiliale && (
                          <div style={{ padding: '6px 12px', fontSize: 11, color: '#C9A84C', fontWeight: 600 }}>
                            🏬 {myFiliale.nom}
                          </div>
                        )}
                        <div style={{ ...s.deptItem(true), cursor: 'default' }}>
                          📂 {myDept?.nom || 'Mon département'}
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>

          {/* MAIN */}
          <div style={s.main}>
            <div style={s.toolbar}>
              <div style={{ fontSize: 12, color: '#667' }}>
                {loading ? 'Chargement...' : `${documents.length} document${documents.length !== 1 ? 's' : ''}`}
              </div>
            </div>

            {!selectedFiliale && isDG && (
              <div style={s.empty}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📁</div>
                Sélectionnez une filiale pour voir ses documents
              </div>
            )}

            {(selectedFiliale || !isDG) && !loading && documents.length === 0 && (
              <div style={s.empty}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                Aucun document dans cette section
              </div>
            )}

            {documents.map(doc => (
              <div key={doc.id} style={s.card}>
                <div style={s.docIcon}>{MIME_ICONS[doc.type_mime] || '📎'}</div>
                <div style={s.docInfo}>
                  <div style={s.docName}>{doc.nom}</div>
                  <div style={s.docMeta}>
                    {doc.departement?.nom && <span style={{ marginRight: 10 }}>📂 {doc.departement.nom}</span>}
                    <span style={{ marginRight: 10 }}>👤 {doc.uploadeur?.prenom} {doc.uploadeur?.nom}</span>
                    <span style={{ marginRight: 10 }}>📅 {formatDate(doc.created_at)}</span>
                    <span>{formatTaille(doc.taille)}</span>
                  </div>
                  {doc.description && <div style={{ fontSize: 11, color: '#556', marginTop: 4 }}>{doc.description}</div>}
                </div>
                {(peutVoir || peutGerer) && (
                  <div style={s.docActions}>
                    <button style={s.btnGreen} onClick={() => handlePreview(doc)}>👁 Voir</button>
                    {peutGerer && (
                      <>
                        <button style={s.btnBlue} onClick={() => handleDownload(doc)}>⬇ Télécharger</button>
                        {isDG && <button style={{ ...s.btnOutline, fontSize: 11, padding: '5px 10px' }} onClick={() => openAcces(doc)}>🔑 Accès</button>}
                        <button style={s.btnDanger} onClick={() => handleDelete(doc.id)}>🗑 Supprimer</button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* MODAL PREVIEW */}
        {previewDoc && (
          <div style={s.previewOverlay} onClick={closePreview}>
            <div style={s.previewHeader} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>{MIME_ICONS[previewDoc.type_mime] || '📎'}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{previewDoc.nom}</div>
                  <div style={{ fontSize: 10, color: '#667' }}>{previewDoc.nom_fichier} · {formatTaille(previewDoc.taille)}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {peutGerer && <button style={s.btnBlue} onClick={() => handleDownload(previewDoc)}>⬇ Télécharger</button>}
                <button style={s.btnOutline} onClick={closePreview}>✕ Fermer</button>
              </div>
            </div>
            <div style={s.previewBody} onClick={e => e.stopPropagation()}>
              {previewLoading && <div style={{ color: '#667', fontSize: 13 }}>Chargement...</div>}
              {!previewLoading && previewUrl && previewDoc.type_mime?.startsWith('image/') && (
                <img src={previewUrl} alt={previewDoc.nom} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, objectFit: 'contain' }} />
              )}
              {!previewLoading && previewUrl && previewDoc.type_mime === 'application/pdf' && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexShrink: 0 }}>
                    <button style={s.btnOutline} onClick={() => setPageNumber(p => Math.max(1, p - 1))} disabled={pageNumber <= 1}>‹</button>
                    <span style={{ fontSize: 12, color: '#aaa' }}>Page {pageNumber} / {numPages || '...'}</span>
                    <button style={s.btnOutline} onClick={() => setPageNumber(p => Math.min(numPages, p + 1))} disabled={pageNumber >= numPages}>›</button>
                  </div>
                  <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center' }}>
                    <Document
                      file={previewUrl}
                      onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                      loading={<div style={{ color: '#667', fontSize: 13, padding: 20 }}>Chargement du PDF...</div>}
                    >
                      <Page
                        pageNumber={pageNumber}
                        width={Math.min(window.innerWidth * 0.75, 800)}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </Document>
                  </div>
                </div>
              )}
              {!previewLoading && isOffice(previewDoc.type_mime) && (
                <iframe
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(getFilePublicUrl(previewDoc))}&embedded=true`}
                  title={previewDoc.nom}
                  style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
                />
              )}
              {!previewLoading && !previewUrl && !isPreviewable(previewDoc.type_mime) && !isOffice(previewDoc.type_mime) && (
                <div style={{ textAlign: 'center', color: '#667' }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>{MIME_ICONS[previewDoc.type_mime] || '📎'}</div>
                  <div style={{ fontSize: 13, marginBottom: 16 }}>Aperçu non disponible pour ce type de fichier</div>
                  <button style={s.btnBlue} onClick={() => handleDownload(previewDoc)}>⬇ Télécharger pour ouvrir</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* MODAL ACCES */}
        {accesDoc && (
          <div style={s.overlay} onClick={() => setAccesDoc(null)}>
            <div style={{ ...s.modal, width: 520, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
              <div style={s.modalTitle}>🔑 Accès au document : {accesDoc.nom}</div>
              {accesLoading ? <div style={{ color: '#667', fontSize: 13 }}>Chargement...</div> : (
                <>
                  <input
                    style={{ ...s.input, marginBottom: 12 }}
                    placeholder="Rechercher un employé..."
                    value={accesSearch}
                    onChange={e => setAccesSearch(e.target.value)}
                  />
                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    {accesUsers
                      .filter(u => !['dg', 'admin'].includes(u.role))
                      .filter(u => {
                        const q = accesSearch.toLowerCase();
                        return !q || `${u.prenom} ${u.nom} ${u.name}`.toLowerCase().includes(q);
                      })
                      .map(u => {
                        const hasAcces = accesListe.some(a => a.id === u.id);
                        return (
                          <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 4px', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                            <div>
                              <div style={{ fontSize: 12, color: '#fff' }}>{u.prenom || ''} {u.nom || u.name}</div>
                              <div style={{ fontSize: 10, color: '#556' }}>{u.email}</div>
                            </div>
                            <button
                              onClick={() => toggleAcces(u.id, hasAcces)}
                              style={hasAcces ? s.btnDanger : s.btnGreen}
                            >
                              {hasAcces ? '❌ Révoquer' : '✅ Accorder'}
                            </button>
                          </div>
                        );
                      })}
                  </div>
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <button style={s.btnOutline} onClick={() => setAccesDoc(null)}>Fermer</button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL UPLOAD */}
        {showModal && (
          <div style={s.overlay} onClick={() => setShowModal(false)}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
              <div style={s.modalTitle}>📤 Ajouter un document</div>
              {err && <div style={s.err}>{err}</div>}

              <div style={s.field}>
                <label style={s.label}>Nom du document {fichiers.length <= 1 ? '*' : '(optionnel — nom du fichier utilisé par défaut)'}</label>
                <input style={s.input} value={form.nom} onChange={e => setForm(p => ({ ...p, nom: e.target.value }))} placeholder="Ex: Rapport mensuel juin 2026" />
              </div>

              <div style={s.field}>
                <label style={s.label}>Filiale *</label>
                <select style={s.select} value={form.filiale_id} onChange={e => setForm(p => ({ ...p, filiale_id: e.target.value, departement_id: '' }))}>
                  <option value="">-- Choisir une filiale --</option>
                  {filiales.map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
                </select>
              </div>

              <div style={s.field}>
                <label style={s.label}>Département (optionnel)</label>
                <select style={s.select} value={form.departement_id} onChange={e => setForm(p => ({ ...p, departement_id: e.target.value }))}>
                  <option value="">-- Tous les départements --</option>
                  {departements.filter(d => String(d.filiale_id) === String(form.filiale_id)).map(d => (
                    <option key={d.id} value={d.id}>{d.nom}</option>
                  ))}
                </select>
              </div>

              <div style={s.field}>
                <label style={s.label}>Description (optionnel)</label>
                <input style={s.input} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Courte description..." />
              </div>

              <div style={s.field}>
                <label style={s.label}>Fichiers *</label>
                <div
                  onClick={() => fileRef.current.click()}
                  style={{ border: '2px dashed rgba(255,255,255,.15)', borderRadius: 8, padding: '14px', cursor: 'pointer', background: 'rgba(255,255,255,.02)' }}
                >
                  <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={e => setFichiers(Array.from(e.target.files))} />
                  {fichiers.length === 0
                    ? <div style={{ textAlign: 'center', fontSize: 12, color: '#667' }}>Cliquer pour choisir un ou plusieurs fichiers</div>
                    : <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {fichiers.map((f, i) => (
                          <div key={i} style={{ fontSize: 11, color: '#66bb6a', display: 'flex', justifyContent: 'space-between' }}>
                            <span>📎 {f.name}</span>
                            <span style={{ color: '#667' }}>{formatTaille(f.size)}</span>
                          </div>
                        ))}
                        <div style={{ fontSize: 10, color: '#556', marginTop: 4, textAlign: 'right' }}>
                          {fichiers.length} fichier{fichiers.length > 1 ? 's' : ''} — {formatTaille(fichiers.reduce((a, f) => a + f.size, 0))} total
                        </div>
                      </div>
                  }
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                <button style={s.btnOutline} onClick={() => setShowModal(false)}>Annuler</button>
                <button style={s.btnGold} onClick={handleUpload} disabled={uploading}>
                  {uploading ? 'Envoi...' : `📤 Envoyer${fichiers.length > 1 ? ` (${fichiers.length})` : ''}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
